import { AbiCoder } from 'ethers'

export interface DecodedParam {
  name: string
  value: string | any
  type: string
}

/**
 * ABI 입력 데이터를 디코딩하여 매개변수 정보를 반환합니다.
 * 
 * @param typeList - ABI 타입 배열 (예: ['address', 'uint256', 'address', 'uint256'])
 * @param hexInputData - 16진수 입력 데이터 (0x 접두사 포함)
 * @param fieldsList - 매개변수 이름 배열 (예: ['userWallet', 'characterId', 'tokenAddress', 'tokenId'])
 * @returns 디코딩된 매개변수 배열 또는 null (실패 시)
 */
export function decodeAbiInputData(
  typeList: string[], 
  hexInputData: string, 
  fieldsList: string[]
): DecodedParam[] | null {
  try {
    if (!typeList.length || !hexInputData || !fieldsList.length) {
      return null
    }

    if (typeList.length !== fieldsList.length) {
      console.warn('Type list and fields list length mismatch')
      return null
    }

    const abiCoder = new AbiCoder()
    const decoded = abiCoder.decode(typeList, hexInputData)
    
    return fieldsList.map((name, index) => ({
      name,
      value: typeof decoded[index] === 'bigint' ? decoded[index].toString() : decoded[index],
      type: typeList[index]
    }))
  } catch (error) {
    console.error('Failed to decode ABI input data:', error)
    return null
  }
}

/**
 * 함수 시그니처에서 매개변수 이름만 추출합니다.
 * 
 * @param functionSignature - 함수 시그니처 (예: 'depositItemFromOwner(address userWallet, uint256 characterId, address tokenAddress, uint256 tokenId)')
 * @returns 매개변수 이름 배열 (예: ['userWallet', 'characterId', 'tokenAddress', 'tokenId'])
 */
export function extractParameterNames(functionSignature: string): string[] {
  const paramsMatch = functionSignature.match(/\((.*)\)/)
  if (!paramsMatch || !paramsMatch[1]) {
    return []
  }
  
  return paramsMatch[1]
    .split(',')
    .map(param => param.trim())
    .filter(param => param.length > 0)
    .map(param => {
      // 배열 타입 (예: uint256[] memory items) 처리를 위해 마지막 단어만 추출
      const match = param.match(/(\w+)$/)
      return match ? match[1] : ''
    })
    .filter(name => name.length > 0)
}

export interface FormattedAbiInfo {
  functionLine?: string
  methodIdLine?: string
  inputDataLines: string[]
}

/**
 * ABI 정보를 사용자 친화적인 형태로 포맷팅합니다.
 * 
 * @param functionSignature - 함수 시그니처 (선택적)
 * @param method - 메소드 ID (선택적)
 * @param inputData - 입력 데이터 (0x 접두사 포함)
 * @returns 포맷팅된 ABI 정보
 */
export function formatAbiInfo(
  functionSignature?: string,
  method?: string,
  inputData?: string
): FormattedAbiInfo {
  const result: FormattedAbiInfo = {
    inputDataLines: []
  }

  // Function 라인 생성
  if (functionSignature) {
    result.functionLine = `Function: ${functionSignature}`
  }

  // MethodID 라인 생성
  if (method) {
    result.methodIdLine = `MethodID: ${method}`
  }

  // Input Data 라인들 생성
  if (inputData) {
    // 0x 제거 후 앞 10자리(메소드 ID) 제거
    const cleanData = inputData.replace(/^0x/, '').slice(10)
    
    // 64자씩 잘라서 인덱스와 함께 포맷팅
    const chunks: string[] = []
    for (let i = 0; i < cleanData.length; i += 64) {
      chunks.push(cleanData.slice(i, i + 64))
    }

    result.inputDataLines = chunks.map((chunk, index) => 
      `[${index}]:  ${chunk.padEnd(64, '0')}`
    )
  }

  return result
}

/**
 * Hex 데이터를 UTF-8 문자열로 변환합니다.
 * 
 * @param hexData - 16진수 데이터 (0x 접두사 포함 또는 제외)
 * @returns UTF-8로 변환된 문자열
 */
export function hexToUtf8(hexData: string): string {
  try {
    // 0x 접두사 제거
    const hex = hexData.startsWith('0x') ? hexData.slice(2) : hexData
    
    // hex를 바이트 배열로 변환한 후 문자로 변환
    const bytes = hex.match(/.{1,2}/g)?.map(byte => parseInt(byte, 16)) || []
    return bytes.map(byte => String.fromCharCode(byte)).join('')
  } catch (error) {
    console.warn('Hex to UTF-8 변환 실패:', error)
    return hexData
  }
}

/**
 * 포맷팅된 ABI 정보를 문자열로 변환합니다.
 * 
 * @param formattedInfo - formatAbiInfo 함수의 결과
 * @returns 한 줄로 연결된 문자열
 */
export function stringifyFormattedAbiInfo(formattedInfo: FormattedAbiInfo): string {
  const lines: string[] = []
  
  if (formattedInfo.functionLine) {
    lines.push(formattedInfo.functionLine + '\n')
  }
  
  if (formattedInfo.methodIdLine) {
    lines.push(formattedInfo.methodIdLine)
  }
  
  lines.push(...formattedInfo.inputDataLines)
  
  return lines.join('\n')
}
